/**
 * Choice Helper - Interactive drone license advisor
 * Products are now configured per drone option
 */

(function() {
  'use strict';

  const ChoiceHelper = {
    state: {
      startMethod: 'by_model',
      selectedDroneKey: null,
      selectedWeightBlockId: null,
      selectedSpecial: 'no'
    },
    
    droneData: [],
    weightData: [],
    currentDrone: null,
    currentWeight: null,

    init() {
      this.cacheSelectors();
      this.loadDroneData();
      this.bindEvents();
      this.updateRecommendations();
    },

    cacheSelectors() {
      this.container = document.querySelector('[data-section-type="choice-helper"]');
      if (!this.container) return;

      this.toggleButtons = this.container.querySelectorAll('[data-method]');
      this.routeByModel = this.container.querySelector('[data-route="by_model"]');
      this.routeByWeight = this.container.querySelector('[data-route="by_weight"]');
      this.manufacturerSelect = this.container.querySelector('.manufacturer-select');
      this.weightButtons = this.container.querySelectorAll('.weight-button');
      this.specialButtons = this.container.querySelectorAll('.special-button');
      this.recommendationsContainer = this.container.querySelector('[data-recommendations-container]');
      this.emptyState = this.container.querySelector('[data-empty-state]');
      this.noResults = this.container.querySelector('.no-results');
      this.productCardsWrapper = this.container.querySelector('[data-product-cards]');
      this.specialWarning = this.container.querySelector('[data-special-warning]');
    },

    loadDroneData() {
      if (!this.container) return;
      
      try {
        const droneDataAttr = this.container.dataset.droneData;
        if (droneDataAttr) {
          this.droneData = JSON.parse(droneDataAttr);
        }
        
        const weightDataAttr = this.container.dataset.weightData;
        if (weightDataAttr) {
          this.weightData = JSON.parse(weightDataAttr);
        }
      } catch (e) {
        console.error('Failed to parse data:', e);
        this.droneData = [];
        this.weightData = [];
      }
    },

    bindEvents() {
      if (!this.container) return;

      // Null-safe event binding for toggle buttons
      if (this.toggleButtons && this.toggleButtons.length > 0) {
        this.toggleButtons.forEach(btn => {
          if (btn) btn.addEventListener('click', (e) => this.handleMethodToggle(e));
        });
      }

      // Manufacturer select (already has null check)
      if (this.manufacturerSelect) {
        this.manufacturerSelect.addEventListener('change', (e) => this.handleModelSelect(e));
      }

      // Null-safe event binding for weight buttons
      if (this.weightButtons && this.weightButtons.length > 0) {
        this.weightButtons.forEach(btn => {
          if (btn) btn.addEventListener('click', (e) => this.handleWeightSelect(e));
        });
      }

      // Null-safe event binding for special buttons
      if (this.specialButtons && this.specialButtons.length > 0) {
        this.specialButtons.forEach(btn => {
          if (btn) btn.addEventListener('click', (e) => this.handleSpecialSelect(e));
        });
      }
    },

    handleMethodToggle(e) {
      const method = e.currentTarget.dataset.method;
      
      // Null-safe toggle active class
      if (this.toggleButtons && this.toggleButtons.length > 0) {
        this.toggleButtons.forEach(btn => {
          if (btn) btn.classList.toggle('active', btn.dataset.method === method);
        });
      }

      // Null-safe route display toggle
      if (method === 'by_model') {
        if (this.routeByModel) {
          this.routeByModel.style.display = 'block';
          this.routeByModel.classList.add('active');
        }
        if (this.routeByWeight) {
          this.routeByWeight.style.display = 'none';
          this.routeByWeight.classList.remove('active');
        }
      } else {
        if (this.routeByModel) {
          this.routeByModel.style.display = 'none';
          this.routeByModel.classList.remove('active');
        }
        if (this.routeByWeight) {
          this.routeByWeight.style.display = 'block';
          this.routeByWeight.classList.add('active');
        }
      }

      this.state.startMethod = method;
      
      if (method === 'by_model') {
        this.state.selectedWeightBlockId = null;
        this.currentWeight = null;
        if (this.weightButtons && this.weightButtons.length > 0) {
          this.weightButtons.forEach(btn => {
            if (btn) btn.classList.remove('active');
          });
        }
      } else {
        this.state.selectedDroneKey = null;
        this.currentDrone = null;
        if (this.manufacturerSelect) this.manufacturerSelect.value = '';
      }

      this.updateRecommendations();
    },

    handleModelSelect(e) {
      const selectedOption = e.target.options[e.target.selectedIndex];
      
      if (!selectedOption || !selectedOption.value) {
        this.state.selectedDroneKey = null;
        this.currentDrone = null;
      } else {
        this.state.selectedDroneKey = selectedOption.dataset.droneKey || null;
        
        // Find drone data
        this.currentDrone = this.droneData.find(d => d.droneKey === this.state.selectedDroneKey);
        
        // Auto-set weight from drone data
        this.state.selectedWeight = selectedOption.dataset.weight || null;
      }

      this.updateRecommendations();
    },

    handleWeightSelect(e) {
      const blockId = e.currentTarget.dataset.blockId;
      
      // Null-safe button toggle
      if (this.weightButtons && this.weightButtons.length > 0) {
        this.weightButtons.forEach(btn => {
          if (btn) btn.classList.toggle('active', btn.dataset.blockId === blockId);
        });
      }

      this.state.selectedWeightBlockId = blockId;
      this.state.selectedDroneKey = null;
      this.currentDrone = null;
      
      // Find weight data
      this.currentWeight = this.weightData.find(w => w.blockId === blockId);
      
      this.updateRecommendations();
    },

    handleSpecialSelect(e) {
      const special = e.currentTarget.dataset.special;
      
      // Null-safe button toggle
      if (this.specialButtons && this.specialButtons.length > 0) {
        this.specialButtons.forEach(btn => {
          if (btn) btn.classList.toggle('active', btn.dataset.special === special);
        });
      }

      this.state.selectedSpecial = special;
      
      this.updateRecommendations();
    },

    updateRecommendations() {
      // Early return if container doesn't exist
      if (!this.container) return;
      
      // Check if user has made a selection
      const hasSelection = (this.state.startMethod === 'by_model' && this.state.selectedDroneKey) ||
                          (this.state.startMethod === 'by_weight' && this.state.selectedWeightBlockId);

      // Show empty state if no selection made yet
      if (!hasSelection) {
        if (this.emptyState) this.emptyState.style.display = 'block';
        if (this.recommendationsContainer) this.recommendationsContainer.style.display = 'none';
        if (this.noResults) this.noResults.style.display = 'none';
        return;
      }

      // Hide empty state
      if (this.emptyState) this.emptyState.style.display = 'none';
      if (this.recommendationsContainer) this.recommendationsContainer.style.display = 'block';

      // Render products
      if (this.state.startMethod === 'by_model' && this.currentDrone) {
        this.renderDroneProducts();
      } else if (this.state.startMethod === 'by_weight' && this.currentWeight) {
        this.renderWeightProducts();
      }
    },
    
    renderWeightProducts() {
      if (!this.productCardsWrapper || !this.currentWeight) return;

      // Clear existing products
      this.productCardsWrapper.innerHTML = '';

      // Always hide warning first
      if (this.specialWarning) {
        this.specialWarning.style.display = 'none';
      }

      // Special flights: show only special product with warning
      if (this.state.selectedSpecial === 'yes') {
        this.renderWeightSpecialProduct();
        return;
      }

      // Normal flow: show basic + upgrade products
      this.renderWeightBasicProduct();
      this.renderWeightUpgradeProduct();

      // Fade in
      this.fadeIn();
    },

    renderWeightBasicProduct() {
      const product = this.currentWeight.basicProduct;
      if (!product || !product.title || !product.handle) return;

      const card = this.createProductCard({
        product: product,
        description: this.currentWeight.basicDescription,
        badge: this.currentWeight.basicBadge,
        upsellHint: this.currentWeight.basicUpsellHint,
        isPopular: false,
        isUpgrade: false
      });

      this.productCardsWrapper.appendChild(card);
    },

    renderWeightUpgradeProduct() {
      const product = this.currentWeight.upgradeProduct;
      if (!product || !product.title || !product.handle) return;

      const card = this.createProductCard({
        product: product,
        description: this.currentWeight.upgradeDescription,
        badge: this.currentWeight.upgradeBadge,
        upsellHint: '',
        isPopular: this.currentWeight.upgradeIsPopular,
        isUpgrade: this.currentWeight.upgradeIsUpgrade
      });

      this.productCardsWrapper.appendChild(card);
    },

    renderWeightSpecialProduct() {
      const product = this.currentWeight.specialProduct;
      if (!product || !product.title || !product.handle) {
        this.productCardsWrapper.innerHTML = '<p style="color: #cbd5e1; text-align: center; padding: 2rem;">No special flights product configured for this weight option.</p>';
        return;
      }

      // Show warning
      if (this.specialWarning && this.currentWeight.specialWarning) {
        const warningP = this.specialWarning.querySelector('p');
        if (warningP) {
          warningP.textContent = this.currentWeight.specialWarning;
        }
        this.specialWarning.style.display = 'block';
      }

      const card = this.createProductCard({
        product: product,
        description: this.currentWeight.specialDescription,
        badge: this.currentWeight.specialBadge,
        upsellHint: '',
        isPopular: false,
        isUpgrade: false
      });

      this.productCardsWrapper.appendChild(card);
      this.fadeIn();
    },

    renderDroneProducts() {
      if (!this.productCardsWrapper || !this.currentDrone) return;

      // Clear existing products
      this.productCardsWrapper.innerHTML = '';

      // Always hide warning first
      if (this.specialWarning) {
        this.specialWarning.style.display = 'none';
      }

      // Special flights: show only special product with warning
      if (this.state.selectedSpecial === 'yes') {
        this.renderSpecialProduct();
        return;
      }

      // Normal flow: show basic + upgrade products
      this.renderBasicProduct();
      this.renderUpgradeProduct();

      // Fade in
      this.fadeIn();
    },

    renderBasicProduct() {
      const product = this.currentDrone.basicProduct;
      if (!product || !product.title || !product.handle) return;

      const card = this.createProductCard({
        product: product,
        description: this.currentDrone.basicDescription,
        badge: this.currentDrone.basicBadge,
        upsellHint: this.currentDrone.basicUpsellHint,
        isPopular: false,
        isUpgrade: false
      });

      this.productCardsWrapper.appendChild(card);
    },

    renderUpgradeProduct() {
      const product = this.currentDrone.upgradeProduct;
      if (!product || !product.title || !product.handle) return;

      const card = this.createProductCard({
        product: product,
        description: this.currentDrone.upgradeDescription,
        badge: this.currentDrone.upgradeBadge,
        upsellHint: '',
        isPopular: this.currentDrone.upgradeIsPopular,
        isUpgrade: this.currentDrone.upgradeIsUpgrade
      });

      this.productCardsWrapper.appendChild(card);
    },

    renderSpecialProduct() {
      const product = this.currentDrone.specialProduct;
      if (!product || !product.title || !product.handle) {
        this.productCardsWrapper.innerHTML = '<p style="color: #cbd5e1; text-align: center; padding: 2rem;">No special flights product configured for this drone.</p>';
        return;
      }

      // Show warning
      if (this.specialWarning && this.currentDrone.specialWarning) {
        const warningP = this.specialWarning.querySelector('p');
        if (warningP) {
          warningP.textContent = this.currentDrone.specialWarning;
        }
        this.specialWarning.style.display = 'block';
      }

      const card = this.createProductCard({
        product: product,
        description: this.currentDrone.specialDescription,
        badge: this.currentDrone.specialBadge,
        upsellHint: '',
        isPopular: false,
        isUpgrade: false
      });

      this.productCardsWrapper.appendChild(card);
      this.fadeIn();
    },

    createProductCard(options) {
      const {
        product,
        description,
        badge,
        upsellHint,
        isPopular,
        isUpgrade
      } = options;

      const card = document.createElement('div');
      card.className = 'recommendation-card';
      if (isPopular) card.classList.add('is-popular');
      if (isUpgrade) card.classList.add('upgrade');

      let html = '';

      // Popular badge
      if (isPopular) {
        html += `<div class="popular-badge">Most Popular</div>`;
      }

      // Product title
      html += `<h3 class="card-title">${this.escapeHtml(product.title)}</h3>`;

      // Description
      if (description) {
        html += `<p class="card-description">${this.escapeHtml(description)}</p>`;
      }

      // Badge
      if (badge) {
        html += `<div class="card-badge">${this.escapeHtml(badge)}</div>`;
      }

      // Button
      const productUrl = product.url || `/products/${product.handle}`;
      html += `
        <a href="${productUrl}" class="card-button">
          View Course
          <svg width="18" height="18" viewBox="0 0 21 20" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334"/>
          </svg>
        </a>
      `;

      // Upsell hint
      if (upsellHint) {
        html += `<p class="card-upsell-hint">${this.escapeHtml(upsellHint)}</p>`;
      }

      card.innerHTML = html;
      return card;
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    fadeIn() {
      if (this.recommendationsContainer) {
        this.recommendationsContainer.style.opacity = '0';
        setTimeout(() => {
          this.recommendationsContainer.style.opacity = '1';
        }, 50);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ChoiceHelper.init());
  } else {
    ChoiceHelper.init();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-section-type="choice-helper"]')) {
      ChoiceHelper.init();
    }
  });
})();
